import { type FC, type ReactNode, useMemo, useRef } from 'react';
import { Text } from 'Text';
import { type BrowseStream, type DisplayMode, type FilterIndex, type StreamAccess } from '../BrowseCourses.types';
import { groupByTopic, limitTopicGroups, sortByTitle } from '../filterStreams';
import { useLazyLoadLimit } from '../hooks/useLazyLoadLimit';
import { useMatchesMedia } from '../hooks/useMatchesMedia';
import { PHONE_QUERY } from '../constants';
import { getLessonsCompletedCount } from '../selectors';
import { BrowseCourseCard } from './BrowseCourseCard';

type Props = {
    streams: BrowseStream[];
    index: FilterIndex;
    displayMode: DisplayMode;
    topicFilters: string[];
    otherTopicName: string;
    accessByStreamId: Map<string, StreamAccess>;
    onPressStream: (stream: BrowseStream) => void;
    // Rendered at the end of the first header row: the display mode toggle
    toolbar: ReactNode;
    // Changes when the filters change, which resets the lazy load limit
    resetKey: string;
};

const gridClassName = 'grid grid-cols-1 gap-3.5 sm:grid-cols-2';

export const CourseGrid: FC<Props> = ({
    streams,
    index,
    displayMode,
    topicFilters,
    otherTopicName,
    accessByStreamId,
    onPressStream,
    toolbar,
    resetKey,
}) => {
    const sentinelRef = useRef<HTMLDivElement>(null);
    // A phone has no room for the locked popover, so a locked card falls back to onPress and its modal
    const isPhone = useMatchesMedia(PHONE_QUERY);
    const allTopicGroups = useMemo(
        () => (displayMode === 'topic' ? groupByTopic(streams, index, { otherTopicName, topicFilters }) : []),
        [displayMode, streams, index, otherTopicName, topicFilters],
    );
    // In topic mode, a course shows under each of its topics, so the limit counts cards, not courses
    const total =
        displayMode === 'topic'
            ? allTopicGroups.reduce((count, group) => count + group.streams.length, 0)
            : streams.length;
    const limit = useLazyLoadLimit({ sentinelRef, total, resetKey: `${resetKey}|${displayMode}` });

    const flatStreams = useMemo(
        () => (displayMode === 'flat' ? sortByTitle(streams).slice(0, limit) : []),
        [displayMode, streams, limit],
    );
    const topicGroups = useMemo(() => limitTopicGroups(allTopicGroups, limit), [allTopicGroups, limit]);

    const renderCard = (stream: BrowseStream) => {
        const access = accessByStreamId.get(stream.id);
        const locked = !access?.canLaunch;
        return (
            <BrowseCourseCard
                key={stream.id}
                stream={stream}
                topics={index.topicsByStreamId.get(stream.id) ?? []}
                progressStatus={index.entries.get(stream.id)?.progressStatus ?? 'not_started'}
                lessonsCompletedCount={getLessonsCompletedCount(stream)}
                locked={locked}
                lockedMessage={locked && !stream.coming_soon && !isPhone ? (access?.reasonMessage ?? null) : null}
                onPress={onPressStream}
            />
        );
    };

    return (
        <div>
            {displayMode === 'flat' ? (
                <>
                    <div className="mb-3.5 flex justify-end">{toolbar}</div>
                    <div className={gridClassName}>{flatStreams.map(renderCard)}</div>
                </>
            ) : (
                topicGroups.map((group, i) => (
                    <section key={group.topic} className="mb-7.5">
                        <div className="mb-3.5 flex items-center justify-between gap-2.5">
                            <Text as="h2" uppercase>
                                {group.topic}
                            </Text>
                            {i === 0 && toolbar}
                        </div>
                        <div className={gridClassName}>{group.streams.map(renderCard)}</div>
                    </section>
                ))
            )}
            <div ref={sentinelRef} aria-hidden />
        </div>
    );
};
